import { DEFAULT_CURRENT_STATUS_SEARCH } from "./CurrentStatusSearch";
import classNames from "classnames";
import i18next from "i18next";
import { html } from "lit-html";
import { unsafeHTML } from "lit-html/directives/unsafe-html";
import { useEffectWithCleanup, useEffectWithoutCleanup, useState } from "Shared/haunted/CustomHooks";
import dayjs from "dayjs";
import { useNewFlightDatePicker } from "../searchTab/flights/useNewFlightDatePicker";
import { useRadioToggle } from "../../useRadioToggle";
import { useReduxState } from "Shared/redux/useReduxState";
import { OUTBOUND, INBOUND } from "Services/constants";
import { useStationSelectorDropdown } from "../searchTab/flights/useStationSelectorDropdown";
import { useRef } from "haunted";
import { City, RoutePickerModel } from "../searchTab/flights/RoutePickerModel";
import { stationUtils } from "../searchTab/flights/stationUtils";
import { ref } from "Components/directives/ref";
import { stationSelectorChevron } from "../searchTab/flights/newFlightSvgs";
import { iconButton } from "Components/iconButton";
import { DynamicSettings } from "Components/admin/DynamicSettings";

interface Props {
	culture: string;
	dynamicSettings: DynamicSettings;
	isActive: boolean;
}

// DEVNOTE: This is a temporary solution as required by JS on JET-10985
const isDeepLinkingPossible = false;

export const useFlightStatusTab = (props: Props) => {
	const [countries] = useReduxState("countries");
	const [currentStatusSearch, setCurrentStatusSearch] = useReduxState("currentStatusSearch");
	const [captchaStatus] = useReduxState("captchaStatus");
	const [kontent] = useReduxState("kontent.flightStatus.flightStatusTest");

	const { getRouteModel } = stationUtils();

	const originSelector = useRef<HTMLDivElement>(null);
	const destinationSelector = useRef<HTMLDivElement>(null);
	const renderedCaptchaId = useRef<number>(undefined);

	const [openSelector, setOpenSelector] = useState<"origin" | "destination" | null>(null);
	const [originModel, setOriginModel] = useState<RoutePickerModel>(undefined);
	const [destinationModel, setDestinationModel] = useState<RoutePickerModel>(undefined);
	const [searchBy, setSearchBy] = useState<"route" | "flightNumber">("route");
	const [captchaContainerId] = useState(`captcha-container-status-${Date.now().toString()}`);
	const [loaderElemId] = useState(`status-tab-${Date.now().toString()}`);

	const init = () => {
		const scrollHandler = () => setOpenSelector(null);
		document.addEventListener("scroll", scrollHandler);

		if (!countries?.length || !isDeepLinkingPossible) {
			return () => document.removeEventListener("scroll", scrollHandler);
		}

		const newOriginModel = getRouteModel(countries);
		setOriginModel(newOriginModel);

		return () => document.removeEventListener("scroll", scrollHandler);
	};

	const initCaptcha = () => {
		if (!isDeepLinkingPossible) return;

		const captchaContainer = document.createElement("div");
		captchaContainer.id = captchaContainerId;
		document.body.appendChild(captchaContainer);
		renderedCaptchaId.current = grecaptcha.render(captchaContainer, {
			sitekey: window.JetSmart.Settings.GoogleReCaptchaSiteKey,
			size: "invisible",
			callback: (token: string) => onSubmit(token),
		});
	};

	const radioToggle = useRadioToggle({
		isOn: searchBy === "flightNumber",
		firstOptionText: i18next.t("Ruta"),
		secondOptionText: i18next.t("Número de vuelo"),
		onChange: (isOn: boolean) => setSearchBy(isOn ? "route" : "flightNumber"),
	});

	const originDropdown = useStationSelectorDropdown({
		countries: originModel?.countries,
		culture: props.culture,
		dynamicSettings: props.dynamicSettings,
		isOpen: openSelector === "origin",
		journeyIndex: OUTBOUND,
		originCity: currentStatusSearch.originCity,
		destinationCity: currentStatusSearch.destinationCity,
		onInput: () => setCurrentStatusSearch({ ...currentStatusSearch, originCity: undefined }),
		onClick: (city: City) =>
			setCurrentStatusSearch({ ...currentStatusSearch, originCity: city, destinationCity: undefined }),
	});

	const destinationDropdown = useStationSelectorDropdown({
		countries: destinationModel?.countries,
		culture: props.culture,
		dynamicSettings: props.dynamicSettings,
		isOpen: openSelector === "destination",
		journeyIndex: INBOUND,
		originCity: currentStatusSearch.originCity,
		destinationCity: currentStatusSearch.destinationCity,
		onInput: () => setCurrentStatusSearch({ ...currentStatusSearch, destinationCity: undefined }),
		onClick: (city: City) => setCurrentStatusSearch({ ...currentStatusSearch, destinationCity: city }),
	});

	const departureDate = useNewFlightDatePicker({
		availableSchedules: [],
		culture: props.culture,
		dynamicSettings: props.dynamicSettings,
		isLoading: false,
		isReturnDate: false,
		max: dayjs(new Date()).add(1, "year"),
		min: dayjs(new Date()),
		placeHolder: i18next.t("Fecha"),
		rangeStart: undefined,
		value: currentStatusSearch.date,
		onChange: (date) => setCurrentStatusSearch({ ...currentStatusSearch, date }),
	});

	const startLoading = () => {
		const loaderInstance = window.newRtLoader(`#${loaderElemId}`);
		loaderInstance.startLoader();
		return loaderInstance;
	};

	const stopLoading = (loader: LoaderInstance) => {
		if (!loader) return;
		loader.stopLoader();
		loader.destroy();
	};

	const onSubmit = async (_token: string) => {
		const loader = startLoading();

		// const baseUrl = window.JetSmart.Settings.BookingUrl + "/api/Booking/RetrieveBookingFromCorp";
		// const fetchOptions: RequestInit = {
		// 	method: "POST",
		// 	credentials: "omit",
		// 	mode: "cors",
		// 	body: getPostBody(token),
		// };

		// const fetchParameters = {
		// 	options: fetchOptions,
		// 	url: baseUrl,
		// };

		try {
			// const response = await fetch(fetchParameters.url, fetchParameters.options);
			// if (response.ok) {
			// 	await handleServerResponse(response, loader);
			// }
		} catch (e) {
			stopLoading(loader);
		}
	};

	const handleSubmit = async () => {
		if (!isDeepLinkingPossible) {
			const [language, country] = props.culture.split("-");
			const url = `https://jetsmart.com/${country}/${language}/estado-de-vuelo`;
			window.location.href = url;
			return;
		}

		setSearchBy("route");
		// const isValid = await validator.validate();

		// if (isValid) {
		grecaptcha.execute(renderedCaptchaId.current);
		// }
	};

	const button = iconButton({
		isAction: true,
		icon: html`<i class="jsh-circle-chevron-right1 text-base"></i>`,
		label: i18next.t("Ver estado de tu vuelo"),
		onClick: handleSubmit,
	});

	const applyOriginSelection = () => {
		if (!currentStatusSearch.originCity || currentStatusSearch.destinationCity) return;
		setOpenSelector(null);

		// DEVNOTE Only open selector after model is set - cannot be useEffect, because side effects
		window.setTimeout(() => setOpenSelector("destination"), 100);
	};

	const applyDestinationSelection = () => {
		if (!currentStatusSearch.destinationCity) return;
		setOpenSelector(null);
		if (currentStatusSearch.originCity && currentStatusSearch.destinationCity) departureDate.open();
	};

	const subscribeToClickOutside = () => {
		const onClickOutside = (e: any) => {
			const path = e.path || (e.composedPath && e.composedPath());
			const isClickInside =
				openSelector === "origin"
					? path.indexOf(originSelector.current) > -1
					: path.indexOf(destinationSelector.current) > -1;

			if (!isClickInside) setOpenSelector(null);
		};

		document.addEventListener("click", onClickOutside);

		return () => document.removeEventListener("click", onClickOutside);
	};

	useEffectWithoutCleanup(applyOriginSelection, [currentStatusSearch.originCity?.code]);
	useEffectWithoutCleanup(applyDestinationSelection, [currentStatusSearch.destinationCity?.code]);

	useEffectWithoutCleanup(() => {
		if (currentStatusSearch.originCity && countries?.length) {
			const newDestinationModel = getRouteModel(countries, currentStatusSearch.originCity);
			setDestinationModel(newDestinationModel);
		}
	}, [currentStatusSearch.originCity?.code, countries]);

	useEffectWithCleanup(subscribeToClickOutside, [openSelector]);

	useEffectWithCleanup(init, [countries?.length]);

	useEffectWithoutCleanup(() => {
		// setIsValidated(false);
		setCurrentStatusSearch(DEFAULT_CURRENT_STATUS_SEARCH);
	}, [props.isActive]);

	useEffectWithoutCleanup(() => {
		if (captchaStatus === "loaded" && props.isActive && renderedCaptchaId.current === undefined) {
			initCaptcha();
		}
	}, [captchaStatus, props.isActive]);

	const originTemplate = () =>
		isDeepLinkingPossible
			? html`
					<div
						ref=${ref(originSelector)}
						class=${classNames(
							"relative flex h-[34px] md:h-full w-full items-center rounded-lg border border-solid bg-white px-4",
							"sm:px-6",
							{
								"border-[#c1c1c1]": openSelector !== "origin" && !currentStatusSearch.originCity,
								"border-n-cyan": openSelector === "origin" && !currentStatusSearch.originCity,
								"border-n-blue": currentStatusSearch.originCity,
							}
						)}
						@click=${() => setOpenSelector("origin")}
					>
						<div
							class=${classNames(
								"absolute right-6 top-1/2 -translate-y-1/2 transform tw-hidden",
								"sm:block"
							)}
						>
							${stationSelectorChevron}
						</div>
						${originDropdown.htmlTemplate()}
					</div>
			  `
			: "";

	const destinationTemplate = () =>
		isDeepLinkingPossible
			? html`
					<div
						ref=${ref(destinationSelector)}
						class=${classNames(
							"relative flex h-[34px] md:h-full w-full cursor-pointer items-center rounded-lg border border-solid bg-white px-4",
							"sm:px-6",
							{
								"border-[#c1c1c1]":
									openSelector !== "destination" && !currentStatusSearch.destinationCity,
								"border-n-cyan": openSelector === "destination" && !currentStatusSearch.destinationCity,
								"border-n-blue": currentStatusSearch.destinationCity,
								"pointer-events-none opacity-75": !currentStatusSearch.originCity,
							}
						)}
						@click=${() => setOpenSelector("destination")}
					>
						<div
							class=${classNames(
								"absolute right-4 top-1/2 -translate-y-1/2 transform tw-hidden",
								"sm:block"
							)}
						>
							${stationSelectorChevron}
						</div>
						${destinationDropdown.htmlTemplate()}
					</div>
			  `
			: "";

	const searchByRouteTemplate = () =>
		searchBy === "route"
			? html`
					<div class="grid grid-cols-1 gap-4 md:h-11 md:grid-cols-3">
						${originTemplate()} ${destinationTemplate()} ${departureDate.htmlTemplate()}
					</div>
					<div class="flex flex-col-reverse items-center justify-center gap-4 md:flex-row md:justify-end">
						<div
							class="cursor-pointer text-center text-n-blue underline underline-offset-4"
							@click=${() => setCurrentStatusSearch(DEFAULT_CURRENT_STATUS_SEARCH)}
						>
							${i18next.t("Limpiar")}
						</div>
						${button.htmlTemplate()}
					</div>
			  `
			: "";

	const searchByFlightNumberTemplate = () => (searchBy === "flightNumber" ? html`<div>NO DESIGN</div>` : "");

	const htmlTemplate = () => html`
		<div class="flex flex-col gap-4 sm:p-8">
			<div class="text-xl leading-6 text-n-blue sm:text-[28px]">${unsafeHTML(kontent.contents.title.value)}</div>

			<div class="flex gap-4 rounded-lg bg-[#F0F3F7] p-2 text-sm/5 text-n-blue">
				<div class="mr-4 text-[40px]/none">${unsafeHTML(kontent.contents.searchboxIcon.value)}</div>
				<div class="text-sm">${unsafeHTML(kontent.contents.text.value)}</div>
			</div>

			${isDeepLinkingPossible
				? html`
						<div class="flex gap-2">${radioToggle.htmlTemplate()}<span>@tooltip</span></div>
						${searchByRouteTemplate()} ${searchByFlightNumberTemplate()}
				  `
				: html`<div class="flex w-full justify-end">${button.htmlTemplate()}</div>`}
		</div>
	`;
	return { htmlTemplate };
};
